import { useEffect } from 'react';
import { useMatch } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { API_STUDENT_JOIN_OFFERS } from '@/api/endpoints';
import { STUDENT_JOIN_OFFERS_QUERY_KEY } from '@/api/queryKeys';
import { getProfileIdentity } from '@/auth/session';
import { STORED_OFFERS_SKIPPED_SESSION } from '@/auth/stored';
import { useUserContext } from '@/auth/UserContext';
import { useSwitchProfile } from '@/hooks/useSwitchProfile';
import {
  ROUTE_HOME,
  ROUTE_PROFILE_FAQ,
  ROUTE_PROFILE_SELECTION,
  ROUTE_STUDENT_JOIN_OFFERS,
  ROUTE_TERMS_AND_CONDITIONS,
} from '@/router/routes';
import type { StudentJoinOfferResponse } from '@/types/user';
import { unwrapAxiosResponse } from '@/utils/axios';

interface UserGateState {
  isFetching: boolean;
  redirectPath: string | null;
}

const PENDING: UserGateState = { isFetching: true, redirectPath: null };
const STAY: UserGateState = { isFetching: false, redirectPath: null };

const redirectTo = (path: string): UserGateState => ({
  isFetching: false,
  redirectPath: path,
});

const isOffersSkippedInSession = (): boolean =>
  sessionStorage.getItem(STORED_OFFERS_SKIPPED_SESSION) === 'true';

const handleTermsAndConditions = (
  isUserFetching: boolean,
  hasAgreedToTerms: boolean,
  isOnTermsPage: boolean,
): UserGateState | undefined => {
  if (isUserFetching) {
    return PENDING;
  }

  if (!hasAgreedToTerms) {
    return isOnTermsPage ? STAY : redirectTo(ROUTE_TERMS_AND_CONDITIONS);
  }

  return undefined;
};

const handleJoinOffers = (
  isOnOffersPage: boolean,
  isJoinOffersFetching: boolean,
  joinOffers: StudentJoinOfferResponse[] | undefined,
): UserGateState | undefined => {
  if (isOffersSkippedInSession()) {
    return isOnOffersPage ? redirectTo(ROUTE_HOME) : undefined;
  }

  if (isJoinOffersFetching) {
    return PENDING;
  }

  const hasActiveJoinOffers = (joinOffers?.length ?? 0) > 0;

  if (hasActiveJoinOffers) {
    return isOnOffersPage ? STAY : redirectTo(ROUTE_STUDENT_JOIN_OFFERS);
  }

  return undefined;
};

const handleProfileSelection = (
  isInProfileSelectionFlow: boolean,
  isProfilesFetching: boolean,
  currentYearProfilesCount: number,
  isAutoSwitching: boolean,
): UserGateState | undefined => {
  const hasSelectedProfile = Boolean(getProfileIdentity());

  if (hasSelectedProfile) {
    return undefined;
  }

  if (isProfilesFetching || isAutoSwitching || currentYearProfilesCount === 1) {
    return PENDING;
  }

  return isInProfileSelectionFlow ? STAY : redirectTo(ROUTE_PROFILE_SELECTION);
};

export const useUserGate = (): UserGateState => {
  const isOnTermsPage = useMatch(ROUTE_TERMS_AND_CONDITIONS) !== null;
  const isOnOffersPage = useMatch(ROUTE_STUDENT_JOIN_OFFERS) !== null;
  const isOnProfileSelectionPage = useMatch(ROUTE_PROFILE_SELECTION) !== null;
  const isOnProfileFaqPage = useMatch(ROUTE_PROFILE_FAQ) !== null;
  const isInProfileSelectionFlow =
    isOnProfileSelectionPage || isOnProfileFaqPage;

  const { user, profiles, isUserFetching, isProfilesFetching } =
    useUserContext();

  const hasAgreedToTerms = user?.userSettings?.hasAgreedToTerms ?? false;
  const hasSelectedProfile = Boolean(getProfileIdentity());

  const { data: joinOffers, isFetching: isJoinOffersFetching } = useQuery<
    StudentJoinOfferResponse[]
  >({
    queryKey: STUDENT_JOIN_OFFERS_QUERY_KEY,
    queryFn: () =>
      apiClient
        .get<StudentJoinOfferResponse[]>(API_STUDENT_JOIN_OFFERS)
        .then(unwrapAxiosResponse),
    enabled: hasAgreedToTerms && !isOffersSkippedInSession(),
    staleTime: Infinity,
  });

  const { switchProfile, isPending: isAutoSwitching } = useSwitchProfile();

  const currentYearProfiles =
    profiles?.studyYears.find((studyYear) => studyYear.isCurrent)?.profiles ??
    [];
  const currentYearProfilesCount = currentYearProfiles.length;
  const singleProfileIdentity =
    !hasSelectedProfile && currentYearProfilesCount === 1
      ? currentYearProfiles[0].identity
      : undefined;

  useEffect(() => {
    if (!singleProfileIdentity) {
      return;
    }

    switchProfile(singleProfileIdentity);
  }, [singleProfileIdentity, switchProfile]);

  return (
    handleTermsAndConditions(isUserFetching, hasAgreedToTerms, isOnTermsPage) ??
    handleJoinOffers(isOnOffersPage, isJoinOffersFetching, joinOffers) ??
    handleProfileSelection(
      isInProfileSelectionFlow,
      isProfilesFetching,
      currentYearProfilesCount,
      isAutoSwitching,
    ) ??
    STAY
  );
};
